import { type VerifiableDocumentMark } from "@/domains/documents/hooks/useDocumentVerification";
import type { AvailableFilters, DocumentRowMapped } from "@/stores/documents/repository";
import type { DocumentWithTags } from "./documentTableTypes";
/**
 * The rows a verify write acts on. A list row's `verified_at` is optional, and
 * the write filters on it to decide which rows it actually transitions — so it
 * is resolved here rather than at each call site.
 */
export function verifyMarks(docs: DocumentWithTags[]): VerifiableDocumentMark[] {
  return docs.map((doc) => ({ id: doc.id, verified_at: doc.verified_at ?? null }));
}

export function mapVersionRowToDocumentWithTags(row: DocumentRowMapped): DocumentWithTags {
  return {
    id: String(row.id),
    filename: row.filename,
    file_path: row.file_path,
    building_id: String(row.building_id),
    content_type: row.content_type ?? "",
    size: row.size ?? 0,
    created_at: row.created_at,
    original_created_at: row.original_created_at,
    modified_at: row.modified_at,
    document_content_modified_date: row.document_content_modified_date,
    created_by: row.created_by,
    created_by_name: row.created_by_name,
    created_by_avatar_url: row.created_by_avatar_url,
    created_by_color: row.created_by_color,
    ...versionDocumentState(row),
    project_id: row.project_id,
    categories: row.categories,
    design_disciplines: row.design_disciplines,
    design_stages: row.design_stages,
    linked_issues: row.linked_issues,
  };
}

// getContentTypeFromPath now lives in @/utils/previewUtils (shared with the
// Timeline's version-history preview handler).

/**
 * Build a set of all valid filter IDs from refreshed available filters.
 * Used to detect and remove stale active filters after tag changes.
 */
export function buildValidFilterIdSet(filters: AvailableFilters): Set<string> {
  const validIds = new Set<string>();

  // Projects: project-{id}
  for (const p of filters.projects) {
    validIds.add(`project-${p.id}`);
  }
  // Fallback projectIds (when projects array is empty)
  for (const id of filters.projectIds) {
    validIds.add(`project-${id}`);
  }

  // Categories: category-{name_lowercase}
  for (const c of filters.categories) {
    validIds.add(`category-${c.name.toLowerCase()}`);
  }

  // Filter groups and merged items
  for (const group of filters.filterGroups) {
    validIds.add(`filter-group-${group.id}`);
    for (const item of group.items) {
      const slug = item.categoryName.trim().toLowerCase().replace(/\s+/g, "-");
      validIds.add(`filter-merged-${group.id}-${slug}`);
      // Legacy format
      validIds.add(`filter-item-${item.id}`);
    }
  }

  // Disciplines: construction-{id}
  for (const d of filters.designDisciplines) {
    validIds.add(`construction-${d.id}`);
  }

  // Stages: stage-{id}
  for (const s of filters.designStages) {
    validIds.add(`stage-${s.id}`);
  }

  return validIds;
}

function versionDocumentState(row: DocumentRowMapped) {
  return {
    verified_at: row.verified_at ?? null,
    verified_by_name: row.verified_by_name ?? null,
    state: row.state,
    version: row.version,
    version_group: row.version_group,
    container_type: row.container_type ?? null,
    parent_document_id: nullableDocumentId(row.parent_document_id),
    folder_id: nullableDocumentId(row.folder_id),
  };
}

function nullableDocumentId(value: number | null): string | null {
  return value !== null ? String(value) : null;
}
